/**
 * PKCE (RFC 7636) primitives on WebCrypto — no dependency.
 *
 * The verifier and the `state` are the only things that MUST survive the
 * redirect to Keycloak and back, so they go to `sessionStorage` under a
 * dedicated key: they are single-use, worthless once the code is exchanged, and
 * they are NOT credentials for the API (unlike an access token, which is why
 * that one never touches storage). The transaction is deleted the moment the
 * callback reads it, so a replay finds nothing.
 */

const TX_KEY = 'snxt_onto_pkce_tx';

export interface PkceTransaction {
  codeVerifier: string;
  state: string;
  /** Where to land after the callback (the route the user asked for). */
  returnTo: string;
  /** True when started with prompt=none: a failure must not show an error. */
  silent?: boolean;
}

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** 32 random bytes, base64url — 43 chars, inside the 43..128 the RFC allows. */
export function randomUrlSafeString(byteLength = 32): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

/** S256: base64url(sha256(ascii(verifier))). Keycloak requires S256 here. */
export async function deriveCodeChallenge(codeVerifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier));
  return base64UrlEncode(new Uint8Array(digest));
}

export function saveTransaction(tx: PkceTransaction): void {
  sessionStorage.setItem(TX_KEY, JSON.stringify(tx));
}

/** Read AND delete the pending transaction (single use). */
export function takeTransaction(): PkceTransaction | null {
  const raw = sessionStorage.getItem(TX_KEY);
  sessionStorage.removeItem(TX_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<PkceTransaction>;
    if (!parsed.codeVerifier || !parsed.state) return null;
    return {
      codeVerifier: parsed.codeVerifier,
      state: parsed.state,
      returnTo: parsed.returnTo || '/',
      silent: parsed.silent === true,
    };
  } catch {
    return null;
  }
}
